import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { isGoogleAccountNoPasswordError } from '../utils/authErrors';
import { sellerProfileHasShop } from '../utils/sellerShop';

const SELLER_ID_KEY = 'sellerId';

interface Seller {
  id: string;
  name: string;
  type: 'individual' | 'company';
  logo: string;
  official: boolean;
  status: string;
  rating: number;
  noOfRating: number;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  profileImageUrl?: string;
  phoneNumber?: string;
  nationality?: string;
  currentLocation?: string;
}

interface AuthContextType {
  seller: Seller | null;
  user: User | null;
  token: string | null;
  /** @deprecated Prefer completeSession after POST /auth/login from the login page */
  login: (email: string, password: string) => Promise<void>;
  completeSession: (tokenData: string, userData: Record<string, unknown>) => Promise<boolean>;
  logout: () => void;
  /** True only while restoring session on app boot — not during login submit */
  initializing: boolean;
  setSellerFromCreateShop: (sellerData: { id: string; name: string; type: string; logo: string; official: boolean }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function normalizeSellerFromResponse(data: any): Seller {
  return {
    id: data.id || data._id?.toString?.() || String(data.id),
    name: data.name ?? '',
    type: data.type === 'company' ? 'company' : 'individual',
    logo: data.logo ?? '',
    official: Boolean(data.official),
    status: data.status ?? 'active',
    rating: Number(data.rating) || 0,
    noOfRating: Number(data.noOfRating) || 0,
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [seller, setSeller] = useState<Seller | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('sellerToken'));
  const [initializing, setInitializing] = useState(true);

  /** Fetches current seller profile (requires auth). Returns null if not seller or request fails. */
  const fetchSellerProfile = async (): Promise<Seller | null> => {
    try {
      const response = await api.get('/seller/profile');
      const data = response.data?.data ?? response.data;
      return normalizeSellerFromResponse(data);
    } catch (err: any) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        localStorage.removeItem(SELLER_ID_KEY);
      }
      return null;
    }
  };

  /** Seller shop exists when profile has an id and shop name (logo is optional). */
  const hasSellerShop = (s: Seller | null): boolean =>
    sellerProfileHasShop(s);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('sellerToken');
      if (!savedToken) {
        setSeller(null);
        setUser(null);
        setToken(null);
        setInitializing(false);
        return;
      }

      try {
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

        const profileRes = await api.get('/user/profile');
        const profileData = profileRes.data?.data ?? profileRes.data;
        const role = profileData?.role ?? 'user';

        setUser({
          id: profileData?.id ?? profileData?._id,
          firstName: profileData?.firstName ?? '',
          lastName: profileData?.lastName ?? '',
          email: profileData?.email ?? '',
          role,
          profileImageUrl: profileData?.profileImageUrl,
          phoneNumber: profileData?.phoneNumber,
          nationality: profileData?.nationality,
          currentLocation: profileData?.currentLocation,
        });

        const sellerData = await fetchSellerProfile();
        if (sellerData) {
          localStorage.setItem(SELLER_ID_KEY, sellerData.id);
          setSeller(hasSellerShop(sellerData) ? sellerData : null);
        } else {
          setSeller(null);
        }

        setToken(savedToken);
      } catch (error: any) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('sellerToken');
          localStorage.removeItem(SELLER_ID_KEY);
          delete api.defaults.headers.common['Authorization'];
          setSeller(null);
          setUser(null);
          setToken(null);
        } else {
          setToken(savedToken);
          setSeller(null);
        }
      } finally {
        setInitializing(false);
      }
    };

    initAuth().catch(() => setInitializing(false));
  }, []);

  const completeSession = async (
    tokenData: string,
    userData: Record<string, unknown>
  ): Promise<boolean> => {
    localStorage.setItem('sellerToken', tokenData);
    api.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;

    const userId = (userData.id ?? userData._id) as string | undefined;
    if (userId) {
      localStorage.setItem('sellerUserId', String(userId));
    }

    try {
      const profileRes = await api.get('/user/profile');
      const profileData = profileRes.data?.data ?? profileRes.data;
      if (profileData?.role) {
        userData.role = profileData.role;
      }
    } catch {
      /* optional */
    }

    if (userData.seller) {
      localStorage.setItem(SELLER_ID_KEY, String(userData.seller));
    }
    const sellerData = await fetchSellerProfile();

    setToken(tokenData);
    setUser({
      id: userId as string,
      firstName: String(userData.firstName ?? ''),
      lastName: String(userData.lastName ?? ''),
      email: String(userData.email ?? ''),
      role: String(userData.role ?? 'user'),
      profileImageUrl: userData.profileImageUrl as string | undefined,
      phoneNumber: userData.phoneNumber as string | undefined,
      nationality: userData.nationality as string | undefined,
      currentLocation: userData.currentLocation as string | undefined,
    });
    const shopReady = Boolean(sellerData && hasSellerShop(sellerData));
    setSeller(shopReady ? sellerData : null);
    return shopReady;
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password, type: 'seller' });
      const data = response.data?.data ?? response.data;
      const { user: userData, token: tokenData } = data;

      if (!tokenData || !userData) {
        throw new Error('Invalid login response');
      }

      await completeSession(tokenData, userData);
    } catch (err: any) {
      if (err.response?.status === 403 && err.response?.data?.message?.includes?.('verified')) {
        throw new Error('EMAIL_NOT_VERIFIED');
      }
      if (isGoogleAccountNoPasswordError(err)) {
        throw new Error('NO_PASSWORD');
      }
      throw err;
    }
  };

  const logout = () => {
    setSeller(null);
    setUser(null);
    setToken(null);
    localStorage.removeItem('sellerToken');
    localStorage.removeItem('sellerUserId');
    localStorage.removeItem(SELLER_ID_KEY);
    delete api.defaults.headers.common['Authorization'];
  };

  const setSellerFromCreateShop = (sellerData: { id: string; name: string; type: string; logo: string; official: boolean }) => {
    const normalized = normalizeSellerFromResponse(sellerData);
    setSeller(normalized);
    localStorage.setItem(SELLER_ID_KEY, normalized.id);
  };

  const value = {
    seller,
    user,
    token,
    login,
    completeSession,
    logout,
    initializing,
    setSellerFromCreateShop,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
